import React, { useState, useEffect } from 'react';
import {
  FiPlus, FiTrash2, FiX, FiRepeat, FiEdit2, FiSearch, FiCheckSquare, FiMessageSquare,
  FiList, FiAlertTriangle, FiAlertCircle, FiClock, FiUsers,
} from 'react-icons/fi';
import { listarTarefas, criarTarefa, atualizarTarefa, excluirTarefa, atualizarGrupoTarefa } from '../../services/tarefas';
import { listarClientesNibo, listarUsuariosNiboAPI } from '../../services/rotinas';
import { SETORES_ROTINA, rotuloSetor } from '../../services/rotinasProgramadas';
/* styles: drawer/modais (compartilhados com o Painel); css: a listagem;
   kpi: os cards de resumo, mesmo visual de Procurações/Certificados */
import styles from './Rotinas.module.css';
import css from './Tarefas.module.css';
import kpi from '../clientes/procuracoes/Procuracoes.module.css';

function useFecharComEsc(onClose) {
  useEffect(() => {
    function aoTeclar(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [onClose]);
}

function tarefaVazia() {
  return {
    titulo: '', data_vencimento: '', cliente_nibo_id: null, cliente_nome: '',
    clientes: [], // só usado na criação -- vincular a vários replica a tarefa, um registro por cliente
    recorrencia: 'nenhuma', parcelas_total: '', checklist: [], checklist_obrigatorio: false,
    competencia: '', responsavel: '', observacoes: '', setor: '',
  };
}

function formParaEdicao(tarefa) {
  return {
    titulo: tarefa.titulo || '',
    data_vencimento: (tarefa.data_vencimento || '').slice(0, 10),
    cliente_nibo_id: tarefa.cliente_nibo_id || null,
    cliente_nome: tarefa.cliente_nome || '',
    recorrencia: tarefa.recorrencia || 'nenhuma',
    parcelas_total: tarefa.parcelas_total || '',
    parcela_numero: tarefa.parcela_numero || null,
    checklist: tarefa.checklist || [],
    checklist_obrigatorio: !!tarefa.checklist_obrigatorio,
    competencia: tarefa.competencia || '',
    responsavel: tarefa.responsavel || '',
    observacoes: tarefa.observacoes || '',
    setor: tarefa.setor || '',
  };
}

/* prévia do fim da recorrência parcelada: dado o mês inicial (do vencimento)
   e a quantidade de parcelas, calcula o mês/ano da última ocorrência --
   só aritmética de mês, sem precisar do clamp de dia usado no cálculo real. */
function mesFinalParcelas(dataInicioISO, qtdParcelas) {
  const qtd = parseInt(qtdParcelas, 10);
  if (!dataInicioISO || !qtd || qtd < 1) return null;
  const [ano, mes] = dataInicioISO.slice(0, 10).split('-').map(Number);
  const totalMeses = (mes - 1) + (qtd - 1);
  const anoFinal = ano + Math.floor(totalMeses / 12);
  const mesFinal = (totalMeses % 12) + 1;
  return `${String(mesFinal).padStart(2, '0')}/${anoFinal}`;
}

/* minúsculo e sem acento, pra busca por cliente casar "sao" com "São" */
function normalizarBusca(texto) {
  return (texto || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

function codigoCliente(nomeCliente) {
  const m = (nomeCliente || '').match(/^(\d+)/);
  return m ? m[1] : (nomeCliente || '');
}

function novoItemChecklist() {
  return { id: (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`), texto: '', concluido: false };
}

function formatarData(iso) {
  if (!iso) return '—';
  const [ano, mes, dia] = iso.slice(0, 10).split('-');
  return `${dia}/${mes}/${ano}`;
}

/* ── Drawer lateral de criação/edição (padrão "Nova tarefa" da Nibo):
   título + vencimento (+ cliente opcional, recorrência mensal e
   checklist), salva e fecha sozinho, volta pra listagem já atualizada.
   Com `tarefa` preenchido, vira edição (PATCH em vez de POST). ── */
export function DrawerTarefa({ tarefa, onClose, onSalva }) {
  const [form, setForm]                 = useState(() => tarefa ? formParaEdicao(tarefa) : tarefaVazia());
  const [salvando, setSalvando]         = useState(false);
  const [erro, setErro]                 = useState('');
  const [usuarios, setUsuarios]         = useState([]);
  const [clientes, setClientes]         = useState([]);
  const [buscaCliente, setBuscaCliente] = useState('');

  useFecharComEsc(onClose);

  useEffect(() => {
    listarUsuariosNiboAPI({ apenasAtivos: true }).then(r => setUsuarios(r.items || [])).catch(() => {});
    listarClientesNibo({ top: 200 })
      .then(r => setClientes((r.items || []).slice().sort((a, b) =>
        codigoCliente(a.name).localeCompare(codigoCliente(b.name), 'pt-BR', { numeric: true })
      )))
      .catch(() => {});
  }, []);

  function alternarClienteSelecionado(cliente) {
    setForm(f => {
      const jaSelecionado = f.clientes.some(c => c.id === cliente.id);
      return {
        ...f,
        clientes: jaSelecionado ? f.clientes.filter(c => c.id !== cliente.id) : [...f.clientes, cliente],
      };
    });
  }

  function alternarTodosClientes(listaFiltrada) {
    setForm(f => {
      const todosMarcados = listaFiltrada.length > 0 && listaFiltrada.every(c => f.clientes.some(sel => sel.id === c.id));
      if (todosMarcados) {
        return { ...f, clientes: f.clientes.filter(sel => !listaFiltrada.some(c => c.id === sel.id)) };
      }
      const novos = listaFiltrada.filter(c => !f.clientes.some(sel => sel.id === c.id));
      return { ...f, clientes: [...f.clientes, ...novos] };
    });
  }

  function adicionarItemChecklist() {
    setForm(f => ({ ...f, checklist: [...f.checklist, novoItemChecklist()] }));
  }
  function atualizarTextoItem(i, texto) {
    setForm(f => ({ ...f, checklist: f.checklist.map((it, idx) => idx === i ? { ...it, texto } : it) }));
  }
  function removerItemChecklist(i) {
    setForm(f => ({ ...f, checklist: f.checklist.filter((_, idx) => idx !== i) }));
  }

  const parceladaInvalida = form.recorrencia === 'parcelada' && !(parseInt(form.parcelas_total, 10) >= 2);

  async function excluir() {
    const quem = tarefa.cliente_nome ? ` de ${tarefa.cliente_nome}` : '';
    if (!window.confirm(`Excluir a tarefa "${tarefa.titulo}"${quem} com vencimento em ${formatarData(tarefa.data_vencimento)}? Isso não pode ser desfeito.`)) return;
    setSalvando(true);
    setErro('');
    try {
      await excluirTarefa(tarefa.id);
      onSalva();
    } catch (err) {
      setErro(err.message);
      setSalvando(false);
    }
  }

  async function salvar(e) {
    e.preventDefault();
    if (!form.titulo.trim() || !form.data_vencimento || parceladaInvalida) return;
    if (!form.setor) { setErro('Selecione o setor da tarefa.'); return; }
    setSalvando(true);
    setErro('');
    try {
      const dados = { ...form, checklist: form.checklist.filter(it => it.texto.trim()) };
      if (tarefa) await atualizarTarefa(tarefa.id, dados);
      else await criarTarefa(dados);
      onSalva();
    } catch (err) {
      setErro(err.message);
      setSalvando(false);
    }
  }

  return (
    <div className={styles.drawerOverlay}>
      <div className={styles.drawer} onClick={e => e.stopPropagation()}>
        <div className={styles.drawerHeader}>
          <h2 className={styles.drawerTitulo}>{tarefa ? 'Editar tarefa' : 'Nova tarefa'}</h2>
          <button type="button" className={styles.drawerBtnFechar} onClick={onClose}><FiX size={16} /></button>
        </div>

        <form onSubmit={salvar} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <div className={styles.drawerBody}>
            {erro && (
              <div className={styles.avisoFaixa}>
                {erro}
                <button type="button" className={styles.avisoFechar} onClick={() => setErro('')}><FiX size={14} /></button>
              </div>
            )}

            <div className={styles.campoRoteiro}>
              <label className={styles.campoLabel}>Título *</label>
              <input
                className={styles.campoInput}
                value={form.titulo}
                onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))}
                placeholder="Ex.: Ligar pro cliente sobre..."
                autoFocus
              />
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <div className={styles.campoRoteiro} style={{ flex: 1 }}>
                <label className={styles.campoLabel}>Vencimento *</label>
                <input
                  className={styles.campoInput}
                  type="date"
                  value={form.data_vencimento}
                  onChange={e => setForm(f => ({ ...f, data_vencimento: e.target.value }))}
                />
              </div>
              <div className={styles.campoRoteiro} style={{ flex: 1 }}>
                <label className={styles.campoLabel}>Recorrência</label>
                {tarefa && tarefa.recorrencia === 'parcelada' ? (
                  <input className={styles.campoInput} disabled value={`Parcelada (${tarefa.parcela_numero}/${tarefa.parcelas_total})`} />
                ) : (
                  <select
                    className={styles.campoInput}
                    value={form.recorrencia}
                    onChange={e => setForm(f => ({ ...f, recorrencia: e.target.value }))}
                  >
                    <option value="nenhuma">Não repete</option>
                    <option value="mensal">Mensal (indefinidamente)</option>
                    {!tarefa && <option value="parcelada">Parcelada (qtd. de meses)</option>}
                  </select>
                )}
              </div>
            </div>

            {!tarefa && form.recorrencia === 'parcelada' && (
              <div style={{ display: 'flex', gap: 12 }}>
                <div className={styles.campoRoteiro} style={{ flex: 1 }}>
                  <label className={styles.campoLabel}>Quantidade de parcelas</label>
                  <input
                    className={styles.campoInput}
                    type="number"
                    min={2}
                    value={form.parcelas_total}
                    onChange={e => setForm(f => ({ ...f, parcelas_total: e.target.value }))}
                    placeholder="Ex.: 12"
                  />
                </div>
                <div className={styles.campoRoteiro} style={{ flex: 1 }}>
                  <label className={styles.campoLabel}>Termina em</label>
                  <input
                    className={styles.campoInput}
                    disabled
                    value={mesFinalParcelas(form.data_vencimento, form.parcelas_total) || '—'}
                  />
                </div>
              </div>
            )}

            {tarefa ? (
              <div className={styles.campoRoteiro}>
                <label className={styles.campoLabel}>Cliente (opcional)</label>
                <select
                  className={styles.campoInput}
                  value={form.cliente_nibo_id || ''}
                  onChange={e => {
                    const cliente = clientes.find(c => c.id === e.target.value);
                    setForm(f => ({ ...f, cliente_nibo_id: cliente?.id || null, cliente_nome: cliente?.name || '' }));
                  }}
                >
                  <option value="">Nenhum cliente</option>
                  {clientes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            ) : (
              <div className={styles.campoRoteiro}>
                <label className={styles.campoLabel}>
                  Clientes (opcional -- marque um ou vários, em ordem de código)
                </label>
                <input
                  className={styles.campoInput}
                  value={buscaCliente}
                  onChange={e => setBuscaCliente(e.target.value)}
                  placeholder="Filtrar por código ou nome..."
                  style={{ marginBottom: 8 }}
                />
                {(() => {
                  const clientesFiltrados = clientes.filter(c => !buscaCliente.trim() || c.name.toLowerCase().includes(buscaCliente.toLowerCase()));
                  const todosMarcados = clientesFiltrados.length > 0 && clientesFiltrados.every(c => form.clientes.some(sel => sel.id === c.id));
                  return (
                    <>
                      <label
                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', fontSize: 13, cursor: 'pointer', fontWeight: 600, border: '1px solid #e4edf5', borderBottom: 'none', borderRadius: '8px 8px 0 0' }}
                      >
                        <input
                          type="checkbox"
                          checked={todosMarcados}
                          disabled={clientesFiltrados.length === 0}
                          onChange={() => alternarTodosClientes(clientesFiltrados)}
                        />
                        {buscaCliente.trim() ? 'Vincular todos os filtrados' : 'Vincular todos os clientes'}
                      </label>
                      <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #e4edf5', borderRadius: '0 0 8px 8px' }}>
                        {clientesFiltrados.map(c => {
                          const marcado = form.clientes.some(sel => sel.id === c.id);
                          return (
                            <label
                              key={c.id}
                              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', fontSize: 13, cursor: 'pointer', borderBottom: '1px solid #f0f4fa' }}
                            >
                              <input type="checkbox" checked={marcado} onChange={() => alternarClienteSelecionado(c)} />
                              {c.name}
                            </label>
                          );
                        })}
                      </div>
                    </>
                  );
                })()}
                {form.clientes.length > 0 && (
                  <>
                    <div className={css.selecionados}>
                      {form.clientes.map(c => (
                        <span key={c.id} className={css.selecionado}>
                          {c.name}
                          <button type="button" title="Tirar" onClick={() => alternarClienteSelecionado(c)}><FiX size={11} /></button>
                        </span>
                      ))}
                    </div>
                    <p className={styles.muted} style={{ marginTop: 6 }}>
                      {form.clientes.length === 1
                        ? '1 cliente selecionado.'
                        : `Vai criar ${form.clientes.length} tarefas iguais, uma pra cada cliente selecionado.`}
                    </p>
                  </>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: 12 }}>
              <div className={styles.campoRoteiro} style={{ flex: 1 }}>
                <label className={styles.campoLabel}>Competência (opcional)</label>
                <input
                  className={styles.campoInput}
                  type="month"
                  value={form.competencia}
                  onChange={e => setForm(f => ({ ...f, competencia: e.target.value }))}
                />
              </div>
              <div className={styles.campoRoteiro} style={{ flex: 1 }}>
                <label className={styles.campoLabel}>Responsável (opcional)</label>
                <select
                  className={styles.campoInput}
                  value={form.responsavel}
                  onChange={e => setForm(f => ({ ...f, responsavel: e.target.value }))}
                >
                  <option value="">Ninguém definido</option>
                  {usuarios.map(u => <option key={u.id} value={u.name}>{u.name}</option>)}
                </select>
              </div>
              <div className={styles.campoRoteiro} style={{ flex: 1 }}>
                <label className={styles.campoLabel}>Setor *</label>
                <select className={styles.campoInput} value={form.setor} onChange={e => setForm(f => ({ ...f, setor: e.target.value }))} required>
                  <option value="">Selecione...</option>
                  {SETORES_ROTINA.map(x => <option key={x.v} value={x.v}>{x.l}</option>)}
                </select>
              </div>
            </div>

            <div className={styles.campoRoteiro}>
              <label className={styles.campoLabel}>Observações (opcional)</label>
              <textarea
                className={styles.campoTextarea}
                rows={3}
                value={form.observacoes}
                onChange={e => setForm(f => ({ ...f, observacoes: e.target.value }))}
                placeholder="Detalhes, contexto, links..."
              />
            </div>

            <div className={styles.campoRoteiro}>
              <label className={styles.campoLabel}>Checklist (opcional)</label>
              {form.checklist.map((item, i) => (
                <div key={item.id} style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                  <input
                    className={styles.campoInput}
                    value={item.texto}
                    onChange={e => atualizarTextoItem(i, e.target.value)}
                    placeholder={`Item ${i + 1}`}
                  />
                  <button type="button" className={styles.btnRefresh} onClick={() => removerItemChecklist(i)} title="Remover item">
                    <FiX size={13} />
                  </button>
                </div>
              ))}
              <button type="button" className={styles.btnCancelar} onClick={adicionarItemChecklist} style={{ marginTop: 8 }}>
                <FiPlus size={12} /> Adicionar item
              </button>

              {form.checklist.length > 0 && (
                <label className={styles.toggleWrap} style={{ marginTop: 10 }}>
                  <input
                    type="checkbox"
                    checked={form.checklist_obrigatorio}
                    onChange={e => setForm(f => ({ ...f, checklist_obrigatorio: e.target.checked }))}
                  />
                  <span className={styles.toggleTrilho}><span className={styles.toggleBola} /></span>
                  Exigir checklist completo pra concluir
                </label>
              )}
            </div>
          </div>

          <div className={styles.drawerFooter}>
            {/* excluir direto daqui (ex.: tarefa repetida vista no Painel) */}
            {tarefa && (
              <button type="button" onClick={excluir} disabled={salvando}
                style={{ marginRight: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid #fecaca', background: '#fff',
                  color: '#dc2626', borderRadius: 8, padding: '0 14px', height: 38, font: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                <FiTrash2 size={13} /> Excluir
              </button>
            )}
            <button type="button" className={styles.btnCancelar} onClick={onClose}>Cancelar</button>
            <button type="submit" className={styles.btnSalvar} disabled={salvando || !form.titulo.trim() || !form.data_vencimento || parceladaInvalida || !form.setor}>
              {salvando ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Prazo da tarefa: dias até o vencimento, contados pela data local
   (só dia/mês/ano, sem hora -- senão o fuso jogava a tarefa de hoje pra
   ontem) ── */
function diasAte(iso) {
  if (!iso) return null;
  const [a, m, d] = iso.slice(0, 10).split('-').map(Number);
  const hoje = new Date();
  return Math.round((Date.UTC(a, m - 1, d) - Date.UTC(hoje.getFullYear(), hoje.getMonth(), hoje.getDate())) / 86400000);
}

function Prazo({ iso, concluido }) {
  const dias = diasAte(iso);
  let texto = '';
  let classe = css.prazoNormal;
  if (concluido) { texto = 'concluída'; classe = css.prazoConcluida; }
  else if (dias === null) texto = '';
  else if (dias < 0)  { texto = `há ${-dias} ${dias === -1 ? 'dia' : 'dias'}`; classe = css.prazoAtrasada; }
  else if (dias === 0) { texto = 'hoje'; classe = css.prazoProxima; }
  else { texto = `em ${dias} ${dias === 1 ? 'dia' : 'dias'}`; if (dias <= 7) classe = css.prazoProxima; }
  return (
    <div className={css.colPrazo}>
      <span className={css.prazoData}>{formatarData(iso)}</span>
      {texto && <span className={`${css.prazoTexto} ${classe}`}>{texto}</span>}
    </div>
  );
}

/* "2026-09" -> "09/2026" */
function formatarCompetencia(c) {
  if (!c) return '—';
  const [a, m] = c.slice(0, 7).split('-');
  return m ? `${m}/${a}` : c;
}

/* recorrência, parcela, checklist e observação como etiquetas pequenas
   embaixo do título -- só leitura; quem marca checklist é o Painel */
/* setor da tarefa -- "sem setor" aparece pra lembrar de classificar */
function SeloSetor({ setor }) {
  return (
    <span style={{ fontSize: 10.5, fontWeight: 800, borderRadius: 5, padding: '1px 6px', textTransform: 'uppercase',
      color: setor ? '#1d4ed8' : '#b45309', background: setor ? '#dbeafe' : '#fef3c7' }}>
      {setor ? rotuloSetor(setor) : 'sem setor'}
    </span>
  );
}

function Etiquetas({ tarefa }) {
  const checklist = tarefa.checklist || [];
  const feitos = checklist.filter(i => i.concluido).length;
  return (
    <div className={css.etiquetas}>
      <SeloSetor setor={tarefa.setor} />
      {tarefa.recorrencia === 'mensal' && <span className={css.etiqueta}><FiRepeat size={10} /> mensal</span>}
      {tarefa.recorrencia === 'parcelada' && (
        <span className={css.etiqueta}><FiRepeat size={10} /> parcela {tarefa.parcela_numero}/{tarefa.parcelas_total}</span>
      )}
      {checklist.length > 0 && (
        <span className={css.etiqueta} title={checklist.map(i => `${i.concluido ? '☑' : '☐'} ${i.texto}`).join('\n')}>
          <FiCheckSquare size={10} /> checklist {feitos}/{checklist.length}
        </span>
      )}
      {tarefa.observacoes && (
        <span className={css.etiqueta} title={tarefa.observacoes}><FiMessageSquare size={10} /> obs.</span>
      )}
    </div>
  );
}

/* ── Linha da tabela (uma tarefa/cliente) -- usada na listagem principal
   e dentro do ModalGrupo (aí sem a coluna Tarefa, que é a mesma pra todos).
   Só consulta e cadastro: concluir e marcar checklist ficam no Painel. ── */
function LinhaTarefa({ tarefa, mostrarTitulo = true, onEditar, onExcluir }) {
  return (
    <tr className={tarefa.concluido ? css.linhaConcluida : undefined}>
      <td><Prazo iso={tarefa.data_vencimento} concluido={tarefa.concluido} /></td>
      {mostrarTitulo && (
        <td>
          <div className={css.titulo}>{tarefa.titulo}</div>
          <Etiquetas tarefa={tarefa} />
        </td>
      )}
      <td className={css.cliente}>{tarefa.cliente_nome || <span className={css.vazioCelula}>—</span>}</td>
      <td className={css.secundario}>{tarefa.responsavel || <span className={css.vazioCelula}>—</span>}</td>
      {mostrarTitulo && <td className={css.secundario}>{formatarCompetencia(tarefa.competencia)}</td>}
      <td>
        <div className={css.acoes}>
          <button type="button" className={css.btnIcone} title="Editar" onClick={() => onEditar(tarefa)}>
            <FiEdit2 size={13} />
          </button>
          <button type="button" className={css.btnIcone} title="Excluir" onClick={() => onExcluir(tarefa.id)}>
            <FiTrash2 size={13} />
          </button>
        </div>
      </td>
    </tr>
  );
}

/* ── Detalhes de uma tarefa (aberto pelo "Visualizar" do quadro do cliente
   na aba Vencimentos): tudo que a tarefa tem, sem sair de lá ── */
export function ModalTarefaDetalhe({ tarefa, onClose, onEditar }) {
  useFecharComEsc(onClose);
  const checklist = tarefa.checklist || [];
  const linha = (rotulo, valor) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span className={styles.muted} style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>{rotulo}</span>
      <span style={{ fontSize: 13.5, color: '#0D1F3C' }}>{valor || <span className={css.vazioCelula}>—</span>}</span>
    </div>
  );
  const rec = tarefa.recorrencia === 'mensal' ? 'Mensal'
    : tarefa.recorrencia === 'parcelada' ? `Parcela ${tarefa.parcela_numero}/${tarefa.parcelas_total}` : 'Não repete';
  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalBox} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div>
            <div className={styles.modalTitulo}>{tarefa.titulo}</div>
            <p className={styles.muted} style={{ marginTop: 2 }}>{tarefa.concluido ? 'Concluída' : 'Em aberto'}</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {onEditar && (
              <button type="button" className={styles.btnCancelar} onClick={onEditar}><FiEdit2 size={12} /> Editar</button>
            )}
            <button type="button" className={styles.modalBtnX} onClick={onClose}><FiX size={16} /></button>
          </div>
        </div>
        <div className={styles.modalBody}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '14px 18px' }}>
            {linha('Cliente', tarefa.cliente_nome)}
            {linha('Vencimento', formatarData(tarefa.data_vencimento))}
            {linha('Competência', formatarCompetencia(tarefa.competencia))}
            {linha('Responsável', tarefa.responsavel)}
            {linha('Setor', tarefa.setor ? rotuloSetor(tarefa.setor) : null)}
            {linha('Recorrência', rec)}
          </div>
          {checklist.length > 0 && (
            <div style={{ marginTop: 18 }}>
              <span className={styles.muted} style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
                Checklist {checklist.filter(i => i.concluido).length}/{checklist.length}{tarefa.checklist_obrigatorio ? ' · obrigatório pra concluir' : ''}
              </span>
              <ul style={{ listStyle: 'none', padding: 0, margin: '6px 0 0' }}>
                {checklist.map((i, k) => (
                  <li key={i.id || k} style={{ fontSize: 13, padding: '3px 0', color: i.concluido ? '#047857' : '#0D1F3C' }}>
                    {i.concluido ? '☑' : '☐'} {i.texto}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {tarefa.observacoes && (
            <div style={{ marginTop: 18 }}>
              <span className={styles.muted} style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>Observações</span>
              <p style={{ whiteSpace: 'pre-wrap', fontSize: 13, color: '#0D1F3C', margin: '6px 0 0' }}>{tarefa.observacoes}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Modal com os clientes de uma tarefa vinculada a vários de uma vez --
   a listagem principal mostra só 1 linha agrupada por grupo_id, evitando
   uma tela cheia de linhas iguais quando a tarefa foi vinculada em massa. ── */
function ModalGrupo({ itens, onClose, onEditarGrupo, onEditar, onExcluir }) {
  useFecharComEsc(onClose);
  const concluidos = itens.filter(i => i.concluido).length;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalBox} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div>
            <div className={styles.modalTitulo}>{itens[0].titulo}</div>
            <p className={styles.muted} style={{ marginTop: 2 }}>
              {itens.length} clientes vinculados · {concluidos}/{itens.length} concluídas
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button type="button" className={styles.btnCancelar} onClick={onEditarGrupo}>
              <FiEdit2 size={12} /> Editar tarefa
            </button>
            <button type="button" className={styles.modalBtnX} onClick={onClose}><FiX size={16} /></button>
          </div>
        </div>
        <div className={styles.modalBody}>
          <table className={css.tabela}>
            <thead>
              <tr>
                <th style={{ width: 130 }}>Vencimento</th>
                <th>Cliente</th>
                <th style={{ width: 160 }}>Responsável</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {itens.map(i => (
                <LinhaTarefa key={i.id} tarefa={i} mostrarTitulo={false} onEditar={onEditar} onExcluir={onExcluir} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ── Edição em massa: título/competência/responsável/observações/checklist
   valem pra todas as tarefas do grupo de uma vez, e dá pra vincular clientes
   novos ao mesmo grupo. Vencimento e conclusão ficam de fora de propósito --
   cada cliente pode já estar num ponto diferente do próprio ciclo (ex.:
   recorrência mensal avança independente por cliente). ── */
export function DrawerEditarGrupo({ itens, onClose, onSalva }) {
  const base = itens[0];
  const ehParcelada = base.recorrencia === 'parcelada';
  const idsJaVinculados = new Set(itens.map(t => t.cliente_nibo_id).filter(Boolean));

  const [titulo, setTitulo]                 = useState(base.titulo || '');
  const [competencia, setCompetencia]       = useState(base.competencia || '');
  const [responsavel, setResponsavel]       = useState(base.responsavel || '');
  const [setor, setSetor]                   = useState(base.setor || '');
  const [observacoes, setObservacoes]       = useState(base.observacoes || '');
  const [checklistObrigatorio, setChecklistObrigatorio] = useState(!!base.checklist_obrigatorio);
  const [checklist, setChecklist]           = useState(base.checklist || []);
  const [usuarios, setUsuarios]             = useState([]);
  const [clientes, setClientes]             = useState([]);
  const [buscaCliente, setBuscaCliente]     = useState('');
  const [novosClientes, setNovosClientes]   = useState([]);
  const [vencimentoNovos, setVencimentoNovos] = useState('');
  const [salvando, setSalvando]             = useState(false);
  const [erro, setErro]                     = useState('');

  useFecharComEsc(onClose);

  useEffect(() => {
    listarUsuariosNiboAPI({ apenasAtivos: true }).then(r => setUsuarios(r.items || [])).catch(() => {});
    listarClientesNibo({ top: 200 })
      .then(r => setClientes((r.items || []).slice().sort((a, b) =>
        codigoCliente(a.name).localeCompare(codigoCliente(b.name), 'pt-BR', { numeric: true })
      )))
      .catch(() => {});
  }, []);

  function adicionarItemChecklist() { setChecklist(cs => [...cs, novoItemChecklist()]); }
  function atualizarTextoItem(i, texto) { setChecklist(cs => cs.map((it, idx) => idx === i ? { ...it, texto } : it)); }
  function removerItemChecklist(i) { setChecklist(cs => cs.filter((_, idx) => idx !== i)); }

  function alternarNovoCliente(cliente) {
    setNovosClientes(ns => ns.some(c => c.id === cliente.id) ? ns.filter(c => c.id !== cliente.id) : [...ns, cliente]);
  }

  const clientesDisponiveis = clientes.filter(c => !idsJaVinculados.has(c.id));
  const clientesFiltrados = clientesDisponiveis.filter(c => !buscaCliente.trim() || c.name.toLowerCase().includes(buscaCliente.toLowerCase()));
  const todosNovosMarcados = clientesFiltrados.length > 0 && clientesFiltrados.every(c => novosClientes.some(sel => sel.id === c.id));

  function alternarTodosNovosClientes() {
    setNovosClientes(ns => {
      if (todosNovosMarcados) return ns.filter(sel => !clientesFiltrados.some(c => c.id === sel.id));
      const novos = clientesFiltrados.filter(c => !ns.some(sel => sel.id === c.id));
      return [...ns, ...novos];
    });
  }

  const invalido = !titulo.trim() || !setor || (novosClientes.length > 0 && !vencimentoNovos);

  async function salvar(e) {
    e.preventDefault();
    if (invalido) return;
    setSalvando(true);
    setErro('');
    try {
      await atualizarGrupoTarefa(base.grupo_id, {
        titulo,
        competencia,
        responsavel,
        setor,
        observacoes,
        checklist: checklist.filter(it => it.texto.trim()),
        checklist_obrigatorio: checklistObrigatorio,
        ...(novosClientes.length > 0 ? { novosClientes, data_vencimento: vencimentoNovos } : {}),
      });
      onSalva();
    } catch (err) {
      setErro(err.message);
      setSalvando(false);
    }
  }

  return (
    <div className={styles.drawerOverlay}>
      <div className={styles.drawer} onClick={e => e.stopPropagation()}>
        <div className={styles.drawerHeader}>
          <h2 className={styles.drawerTitulo}>Editar tarefa ({itens.length} vinculados)</h2>
          <button type="button" className={styles.drawerBtnFechar} onClick={onClose}><FiX size={16} /></button>
        </div>

        <form onSubmit={salvar} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <div className={styles.drawerBody}>
            {erro && (
              <div className={styles.avisoFaixa}>
                {erro}
                <button type="button" className={styles.avisoFechar} onClick={() => setErro('')}><FiX size={14} /></button>
              </div>
            )}

            <p className={styles.muted}>
              Os campos abaixo valem para as {itens.length} tarefas vinculadas. Vencimento e conclusão ficam de fora --
              cada cliente pode estar num ponto diferente do próprio ciclo (ex.: recorrência mensal já avançou pra outro mês em alguns).
            </p>

            <div className={styles.campoRoteiro}>
              <label className={styles.campoLabel}>Título</label>
              <input className={styles.campoInput} value={titulo} onChange={e => setTitulo(e.target.value)} autoFocus />
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <div className={styles.campoRoteiro} style={{ flex: 1 }}>
                <label className={styles.campoLabel}>Competência (opcional)</label>
                <input className={styles.campoInput} type="month" value={competencia} onChange={e => setCompetencia(e.target.value)} />
              </div>
              <div className={styles.campoRoteiro} style={{ flex: 1 }}>
                <label className={styles.campoLabel}>Responsável (opcional)</label>
                <select className={styles.campoInput} value={responsavel} onChange={e => setResponsavel(e.target.value)}>
                  <option value="">Ninguém definido</option>
                  {usuarios.map(u => <option key={u.id} value={u.name}>{u.name}</option>)}
                </select>
              </div>
              <div className={styles.campoRoteiro} style={{ flex: 1 }}>
                <label className={styles.campoLabel}>Setor *</label>
                <select className={styles.campoInput} value={setor} onChange={e => setSetor(e.target.value)} required>
                  <option value="">Selecione...</option>
                  {SETORES_ROTINA.map(x => <option key={x.v} value={x.v}>{x.l}</option>)}
                </select>
              </div>
            </div>

            <div className={styles.campoRoteiro}>
              <label className={styles.campoLabel}>Observações (opcional)</label>
              <textarea
                className={styles.campoTextarea}
                rows={3}
                value={observacoes}
                onChange={e => setObservacoes(e.target.value)}
              />
            </div>

            <div className={styles.campoRoteiro}>
              <label className={styles.campoLabel}>Checklist (opcional)</label>
              {checklist.map((item, i) => (
                <div key={item.id} style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                  <input
                    className={styles.campoInput}
                    value={item.texto}
                    onChange={e => atualizarTextoItem(i, e.target.value)}
                    placeholder={`Item ${i + 1}`}
                  />
                  <button type="button" className={styles.btnRefresh} onClick={() => removerItemChecklist(i)} title="Remover item">
                    <FiX size={13} />
                  </button>
                </div>
              ))}
              <button type="button" className={styles.btnCancelar} onClick={adicionarItemChecklist} style={{ marginTop: 8 }}>
                <FiPlus size={12} /> Adicionar item
              </button>
              {checklist.length > 0 && (
                <label className={styles.toggleWrap} style={{ marginTop: 10 }}>
                  <input
                    type="checkbox"
                    checked={checklistObrigatorio}
                    onChange={e => setChecklistObrigatorio(e.target.checked)}
                  />
                  <span className={styles.toggleTrilho}><span className={styles.toggleBola} /></span>
                  Exigir checklist completo pra concluir
                </label>
              )}
            </div>

            {ehParcelada ? (
              <p className={styles.muted}>
                Essa tarefa é parcelada -- vincular cliente novo não se aplica aqui, porque não daria pra saber a partir de qual parcela ele entraria.
              </p>
            ) : (
              <div className={styles.campoRoteiro}>
                <label className={styles.campoLabel}>Vincular novos clientes ao grupo (opcional)</label>
                <input
                  className={styles.campoInput}
                  value={buscaCliente}
                  onChange={e => setBuscaCliente(e.target.value)}
                  placeholder="Filtrar por código ou nome..."
                  style={{ marginBottom: 8 }}
                />
                <label
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', fontSize: 13, cursor: 'pointer', fontWeight: 600, border: '1px solid #e4edf5', borderBottom: 'none', borderRadius: '8px 8px 0 0' }}
                >
                  <input
                    type="checkbox"
                    checked={todosNovosMarcados}
                    disabled={clientesFiltrados.length === 0}
                    onChange={alternarTodosNovosClientes}
                  />
                  {buscaCliente.trim() ? 'Marcar todos os filtrados' : 'Marcar todos'}
                </label>
                <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #e4edf5', borderRadius: '0 0 8px 8px' }}>
                  {clientesFiltrados.map(c => {
                    const marcado = novosClientes.some(sel => sel.id === c.id);
                    return (
                      <label
                        key={c.id}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', fontSize: 13, cursor: 'pointer', borderBottom: '1px solid #f0f4fa' }}
                      >
                        <input type="checkbox" checked={marcado} onChange={() => alternarNovoCliente(c)} />
                        {c.name}
                      </label>
                    );
                  })}
                  {clientesFiltrados.length === 0 && (
                    <p className={styles.muted} style={{ padding: '8px 10px', margin: 0 }}>
                      Nenhum cliente disponível -- os que já estão no grupo ficam fora da lista.
                    </p>
                  )}
                </div>

                {novosClientes.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <label className={styles.campoLabel}>Vencimento para os {novosClientes.length} novo(s) cliente(s)</label>
                    <input className={styles.campoInput} type="date" value={vencimentoNovos} onChange={e => setVencimentoNovos(e.target.value)} />
                  </div>
                )}
              </div>
            )}
          </div>

          <div className={styles.drawerFooter}>
            <button type="button" className={styles.btnCancelar} onClick={onClose}>Cancelar</button>
            <button type="submit" className={styles.btnSalvar} disabled={salvando || invalido}>
              {salvando ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Busca: só números de até 4 dígitos = código do cliente (mesma regra
   da tela Clientes -- "110" não pode achar tarefa de outro cliente só
   porque o número aparece no meio do nome/título); qualquer outra coisa
   procura no título e no nome do cliente ── */
function casaBusca(tarefa, termo) {
  if (!termo) return true;
  if (/^\d{1,4}$/.test(termo)) {
    const codigo = codigoCliente(tarefa.cliente_nome);
    return codigo.startsWith(termo) || (!termo.startsWith('0') && codigo.replace(/^0+/, '').startsWith(termo));
  }
  return normalizarBusca(tarefa.titulo).includes(termo) || normalizarBusca(tarefa.cliente_nome).includes(termo);
}

/* períodos dos cards do topo -- valem pro vencimento da LINHA (no grupo,
   o vencimento mais próximo entre os clientes ainda em aberto) */
const PERIODOS = {
  abertas:   l => !l.concluida,
  atrasadas: l => !l.concluida && l.dias !== null && l.dias < 0,
  dias7:     l => !l.concluida && l.dias !== null && l.dias >= 0 && l.dias <= 7,
  dias30:    l => !l.concluida && l.dias !== null && l.dias >= 0 && l.dias <= 30,
};

const CARDS = [
  { id: 'abertas',   titulo: 'Em aberto',  sub: 'Todas as pendentes',  icone: FiList,          cor: kpi.kpiAzul },
  { id: 'atrasadas', titulo: 'Atrasadas',  sub: 'Vencimento passou',   icone: FiAlertTriangle, cor: kpi.kpiVermelho },
  { id: 'dias7',     titulo: 'Vencimentos', sub: 'Próximos 7 dias',    icone: FiAlertCircle,   cor: kpi.kpiLaranja },
  { id: 'dias30',    titulo: 'Vencimentos', sub: 'Próximos 30 dias',   icone: FiClock,         cor: kpi.kpiRoxo },
];

function Tarefas() {
  const [tarefas, setTarefas]           = useState([]);
  const [carregando, setCarregando]     = useState(true);
  const [erro, setErro]                 = useState('');
  const [mostrarConcluidas, setMostrarConcluidas] = useState(false);
  const [drawerAberto, setDrawerAberto] = useState(false);
  const [tarefaEditando, setTarefaEditando] = useState(null);
  const [grupoAberto, setGrupoAberto]   = useState(null); // grupo_id do modal aberto
  const [editandoGrupo, setEditandoGrupo] = useState(false); // drawer de edição em massa, sobre o modal
  const [busca, setBusca]               = useState('');
  const [responsavel, setResponsavel]   = useState('');
  const [periodo, setPeriodo]           = useState(null); // card clicado

  function carregar() {
    setCarregando(true);
    listarTarefas()
      .then(r => setTarefas(r.items || []))
      .catch(e => setErro(e.message))
      .finally(() => setCarregando(false));
  }

  useEffect(() => { carregar(); }, []);

  function aoSalvarTarefa() {
    setDrawerAberto(false);
    setTarefaEditando(null);
    carregar();
  }

  async function remover(id) {
    if (!window.confirm('Excluir esta tarefa?')) return;
    setTarefas(ts => ts.filter(x => x.id !== id));
    try {
      await excluirTarefa(id);
    } catch (err) {
      setErro(err.message);
      carregar();
    }
  }

  async function removerGrupo(itens) {
    if (!window.confirm(`Excluir estas ${itens.length} tarefas (uma por cliente)?`)) return;
    const ids = itens.map(x => x.id);
    setTarefas(ts => ts.filter(x => !ids.includes(x.id)));
    setGrupoAberto(null);
    try {
      await Promise.all(ids.map(id => excluirTarefa(id)));
    } catch (err) {
      setErro(err.message);
      carregar();
    }
  }

  const responsaveis = [...new Set(tarefas.map(x => x.responsavel).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'pt-BR'));

  const termo = normalizarBusca(busca.trim());
  const filtradas = tarefas.filter(x =>
    (mostrarConcluidas || !x.concluido) &&
    (!responsavel || x.responsavel === responsavel) &&
    casaBusca(x, termo)
  );

  /* agrupa por grupo_id (tarefa vinculada a vários clientes de uma vez).
     O agrupamento usa só as tarefas que passaram no filtro: buscar pelo
     código de um cliente deixa 1 item no grupo, e grupo de 1 volta a ser
     linha normal com o nome do cliente; buscar pelo título mantém o grupo. */
  const linhas = (() => {
    const grupos = new Map();
    const resultado = [];
    filtradas.forEach(x => {
      if (x.grupo_id) {
        let g = grupos.get(x.grupo_id);
        if (!g) { g = { grupo_id: x.grupo_id, itens: [] }; grupos.set(x.grupo_id, g); resultado.push(g); }
        g.itens.push(x);
      } else {
        resultado.push(x);
      }
    });
    return resultado.map(item => {
      if (item.itens && item.itens.length === 1) item = item.itens[0];
      if (!item.itens) {
        return { chave: item.id, tarefa: item, concluida: !!item.concluido, vencimento: item.data_vencimento, dias: diasAte(item.data_vencimento) };
      }
      const abertos = item.itens.filter(x => !x.concluido);
      const vencimento = (abertos[0] || item.itens[0]).data_vencimento; // backend já manda ordenado por vencimento
      return { chave: item.grupo_id, grupo: item, concluida: abertos.length === 0, vencimento, dias: diasAte(vencimento) };
    });
  })();

  const visiveis = periodo ? linhas.filter(PERIODOS[periodo]) : linhas;
  const itensGrupoAberto = grupoAberto ? tarefas.filter(x => x.grupo_id === grupoAberto) : [];

  return (
    <div className={css.page}>
      <div className={css.header}>
        <div className={css.headerIcone}><FiCheckSquare size={20} /></div>
        <h1 className={css.tituloPagina}>Tarefas</h1>
        <div className={css.buscaWrap}>
          <FiSearch size={14} className={css.buscaIcone} />
          <input
            className={css.campo}
            placeholder="Buscar tarefa ou cliente..."
            value={busca}
            onChange={e => setBusca(e.target.value)}
          />
          {busca && (
            <button type="button" className={css.buscaLimpar} onClick={() => setBusca('')} title="Limpar busca">
              <FiX size={13} />
            </button>
          )}
        </div>
        <button type="button" className={css.btnPrimario} onClick={() => setDrawerAberto(true)}>
          <FiPlus size={14} /> Nova tarefa
        </button>
      </div>

      {/* cards: contam linhas (grupo = 1), e o clique filtra a lista;
          clicar de novo no card ativo volta pra lista inteira */}
      <div className={kpi.kpis}>
        {CARDS.map(c => {
          const Icone = c.icone;
          const ativo = periodo === c.id;
          return (
            <button key={c.id} type="button" className={`${kpi.kpi} ${ativo ? kpi.kpiAtivo : ''}`}
              onClick={() => setPeriodo(ativo ? null : c.id)}>
              <span className={`${kpi.kpiIcone} ${c.cor}`}><Icone size={17} /></span>
              <span className={kpi.kpiTexto}>
                <strong className={kpi.kpiValor}>{linhas.filter(PERIODOS[c.id]).length}</strong>
                <span className={kpi.kpiTitulo}>{c.titulo}</span>
                <span className={kpi.kpiSub}>{c.sub}</span>
              </span>
            </button>
          );
        })}
      </div>

      {erro && (
        <div className={styles.avisoFaixa}>
          {erro}
          <button type="button" className={styles.avisoFechar} onClick={() => setErro('')}><FiX size={14} /></button>
        </div>
      )}

      <div className={css.conteudo}>
        <div className={css.barraFiltros}>
          <label className={css.filtroLabel}>
            Responsável
            <select className={css.campoSelect} value={responsavel} onChange={e => setResponsavel(e.target.value)}>
              <option value="">Todos</option>
              {responsaveis.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
          <label className={css.filtroCheck}>
            <input type="checkbox" checked={mostrarConcluidas} onChange={e => setMostrarConcluidas(e.target.checked)} />
            Mostrar concluídas
          </label>
          <span className={css.contador}>
            {visiveis.length} {visiveis.length === 1 ? 'tarefa' : 'tarefas'}
          </span>
        </div>

        <table className={css.tabela}>
          <thead>
            <tr>
              <th style={{ width: 130 }}>Vencimento</th>
              <th>Tarefa</th>
              <th style={{ width: '30%' }}>Cliente</th>
              <th style={{ width: 150 }}>Responsável</th>
              <th style={{ width: 100 }}>Competência</th>
              <th style={{ width: 80 }}></th>
            </tr>
          </thead>
          <tbody>
            {carregando ? (
              <tr><td colSpan={6} className={css.vazio}>Carregando...</td></tr>
            ) : visiveis.length === 0 ? (
              <tr><td colSpan={6} className={css.vazio}>
                {tarefas.length === 0 ? 'Nenhuma tarefa cadastrada.' : 'Nenhuma tarefa encontrada com esses filtros.'}
              </td></tr>
            ) : visiveis.map(l => l.tarefa ? (
              <LinhaTarefa key={l.chave} tarefa={l.tarefa} onEditar={setTarefaEditando} onExcluir={remover} />
            ) : (
              <tr key={l.chave} className={l.concluida ? css.linhaConcluida : undefined}>
                <td><Prazo iso={l.vencimento} concluido={l.concluida} /></td>
                <td>
                  <button type="button" className={css.tituloLink} onClick={() => setGrupoAberto(l.chave)}>
                    {l.grupo.itens[0].titulo}
                  </button>
                  <Etiquetas tarefa={l.grupo.itens[0]} />
                </td>
                <td>
                  <button type="button" className={css.grupoBtn} onClick={() => setGrupoAberto(l.chave)}>
                    <FiUsers size={12} /> {l.grupo.itens.length} clientes
                  </button>
                  <span className={css.grupoProgresso}>
                    {l.grupo.itens.filter(x => x.concluido).length}/{l.grupo.itens.length} concluídas
                  </span>
                </td>
                <td className={css.secundario}>{l.grupo.itens[0].responsavel || <span className={css.vazioCelula}>—</span>}</td>
                <td className={css.secundario}>{formatarCompetencia(l.grupo.itens[0].competencia)}</td>
                <td>
                  <div className={css.acoes}>
                    <button type="button" className={css.btnIcone} title="Editar tarefa do grupo"
                      onClick={() => { setGrupoAberto(l.chave); setEditandoGrupo(true); }}>
                      <FiEdit2 size={13} />
                    </button>
                    <button type="button" className={css.btnIcone} title="Excluir todas" onClick={() => removerGrupo(l.grupo.itens)}>
                      <FiTrash2 size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(drawerAberto || tarefaEditando) && (
        <DrawerTarefa
          tarefa={tarefaEditando}
          onClose={() => { setDrawerAberto(false); setTarefaEditando(null); }}
          onSalva={aoSalvarTarefa}
        />
      )}

      {grupoAberto && itensGrupoAberto.length > 0 && (
        <ModalGrupo
          itens={itensGrupoAberto}
          onClose={() => setGrupoAberto(null)}
          onEditarGrupo={() => setEditandoGrupo(true)}
          onEditar={setTarefaEditando}
          onExcluir={remover}
        />
      )}

      {editandoGrupo && itensGrupoAberto.length > 0 && (
        <DrawerEditarGrupo
          itens={itensGrupoAberto}
          onClose={() => setEditandoGrupo(false)}
          onSalva={() => { setEditandoGrupo(false); carregar(); }}
        />
      )}
    </div>
  );
}

export default Tarefas;
